// src/components/switches/SwitchList.jsx

import React, { useState } from "react";
import { CLIENT_TAGS } from "../../utils/models/clientModel";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  Paper,
  Stack,
  useTheme,
  TablePagination,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Badge,
  Collapse,
  Button,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  SwapHoriz,
  Business,
  Event,
  EventBusy,
  FilterList,
  FilterListOff,
  Clear,
  LocationOn,
  ElectricBolt,
  LocalFireDepartment,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import ConfirmDialog from "../ui/ConfirmDialog";
import { SUPPLY_TYPES } from "../../utils/models/switchModel";

const SwitchList = ({ onEdit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: switches, loading } = useCollection("switches");
  const { data: suppliers } = useCollection("switchSuppliers");
  const { data: clients } = useCollection("clients");
  const [localSwitches, setLocalSwitches] = useState(null);
  const { remove } = useAuditFirestore("switches");
  
  // Filtros
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [supplyTypeFilter, setSupplyTypeFilter] = useState("all");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const uniqueSuppliers = suppliers?.filter((s) => s.isActive !== false) || [];

  const switchClients = clients?.filter(
    (c) => c.isActive !== false && c.tags?.includes(CLIENT_TAGS.CAMBIO_COMERCIALIZADORA)
  ) || [];

  React.useEffect(() => {
    if (switches) setLocalSwitches(switches);
  }, [switches]);

  React.useEffect(() => {
    setPage(0);
  }, [search, statusFilter, supplyTypeFilter, selectedSupplier, selectedClient, dateFrom, dateTo]);

  const isActiveSwitch = (switchItem) => !switchItem.fechaSalida;

  const filtered = localSwitches?.filter((s) => {
    if (s.isActive === false) return false;

    const matchesSearch = search === "" || 
      s.switchClientName?.toLowerCase().includes(search.toLowerCase()) ||
      s.supplierName?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = statusFilter === "all" ||
      (statusFilter === "active" && isActiveSwitch(s)) ||
      (statusFilter === "finished" && !isActiveSwitch(s));

    const matchesSupplyType = supplyTypeFilter === "all" || 
      (s.supplyType || SUPPLY_TYPES.LUZ) === supplyTypeFilter;

    const matchesSupplier = !selectedSupplier || s.supplierId === selectedSupplier.id;
    const matchesClient = !selectedClient || s.switchClientId === selectedClient.id;

    let matchesDateRange = true;
    if (dateFrom || dateTo) {
      const entradaDate = new Date(s.fechaEntrada);
      if (dateFrom && entradaDate < new Date(dateFrom)) matchesDateRange = false;
      if (dateTo && entradaDate > new Date(dateTo + "T23:59:59")) matchesDateRange = false;
    }

    return matchesSearch && matchesStatus && matchesSupplyType && matchesSupplier && matchesClient && matchesDateRange;
  });

  const sorted = filtered?.sort((a, b) => new Date(b.fechaEntrada) - new Date(a.fechaEntrada));
  const paginatedSwitches = sorted?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getFormattedAddress = (switchItem) => {
    if (switchItem.addressStreet) return switchItem.addressStreet;
    if (switchItem.addressCity) return switchItem.addressCity;
    if (switchItem.addressCups) return `CUPS: ${switchItem.addressCups}`;
    if (switchItem.addressCupsGas) return `CUPS Gas: ${switchItem.addressCupsGas}`;
    return "—";
  };

  const getSupplyTypeChip = (switchItem) => {
    const isGas = (switchItem.supplyType || SUPPLY_TYPES.LUZ) === SUPPLY_TYPES.GAS;
    return (
      <Chip
        icon={isGas ? <LocalFireDepartment sx={{ fontSize: 14 }} /> : <ElectricBolt sx={{ fontSize: 14 }} />}
        label={isGas ? 'Gas' : 'Luz'}
        size="small"
        variant="outlined"
        sx={{
          fontSize: "0.7rem",
          fontWeight: 600,
          height: 24,
          borderColor: isGas ? theme.palette.error.main : theme.palette.warning.main,
          color: isGas ? theme.palette.error.main : theme.palette.warning.main,
          "& .MuiChip-icon": {
            color: isGas ? theme.palette.error.main : theme.palette.warning.main,
            marginLeft: "4px",
          },
        }}
      />
    );
  };

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setSupplyTypeFilter("all");
    setSelectedSupplier(null);
    setSelectedClient(null);
    setDateFrom("");
    setDateTo("");
  };

  const activeFiltersCount = [
    search !== "",
    statusFilter !== "all",
    supplyTypeFilter !== "all",
    selectedSupplier !== null,
    selectedClient !== null,
    dateFrom !== "",
    dateTo !== "",
  ].filter(Boolean).length;

  if (loading || !localSwitches) return <LoadingSpinner />;

  return (
    <Box>
      {/* Barra de búsqueda y filtros */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0, 95, 101, 0.12)",
          background: theme.palette.background.paper,
        }}
      >
        <Stack spacing={2}>
          {/* Barra principal */}
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flex={1}>
              <TextField
                placeholder="Buscar por cliente o comercializadora..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: 280, md: 340 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: theme.palette.background.paper,
                    height: 44,
                    "& fieldset": { borderColor: theme.palette.divider },
                    "&:hover fieldset": { borderColor: theme.palette.primary.main },
                    "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}>✖</IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ width: { xs: "100%", sm: 140 }, height: 44 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Estado"
                  sx={{ borderRadius: "12px", "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider } }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Activos</MenuItem>
                  <MenuItem value="finished">Finalizados</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ width: { xs: "100%", sm: 120 }, height: 44 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={supplyTypeFilter}
                  onChange={(e) => setSupplyTypeFilter(e.target.value)}
                  label="Tipo"
                  sx={{ borderRadius: "12px", "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider } }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value={SUPPLY_TYPES.LUZ}>Luz</MenuItem>
                  <MenuItem value={SUPPLY_TYPES.GAS}>Gas</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Badge badgeContent={activeFiltersCount} color="primary" invisible={activeFiltersCount === 0}>
                <Button
                  variant={showFilters || activeFiltersCount > 0 ? "contained" : "outlined"}
                  size="small"
                  startIcon={showFilters ? <FilterListOff /> : <FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    height: 44,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: theme.palette.primary.main,
                    color: showFilters || activeFiltersCount > 0 ? "white" : theme.palette.primary.main,
                    backgroundColor: showFilters || activeFiltersCount > 0 ? theme.palette.primary.main : "transparent",
                    "&:hover": {
                      backgroundColor: showFilters ? theme.palette.primary.dark : `${theme.palette.primary.main}10`,
                    },
                  }}
                >
                  Filtros
                </Button>
              </Badge>

              <Chip
                icon={<SwapHoriz />}
                label={`${filtered?.length || 0} trámites`}
                sx={{
                  height: 44,
                  borderRadius: "12px",
                  fontWeight: 600,
                  background: `${theme.palette.primary.main}15`,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}40`,
                  "& .MuiChip-icon": { color: theme.palette.primary.main },
                }}
              />

              <Tooltip title="Refrescar">
                <IconButton
                  onClick={() => { if (switches) setLocalSwitches(switches); }}
                  size="small"
                  sx={{ color: theme.palette.primary.main, "&:hover": { backgroundColor: `${theme.palette.primary.main}10` } }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Filtros avanzados */}
          <Collapse in={showFilters}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                {/* Filtro por Comercializadora */}
                <Autocomplete
                  size="small"
                  options={uniqueSuppliers}
                  getOptionLabel={(option) => option.name || ""}
                  value={selectedSupplier}
                  onChange={(event, newValue) => setSelectedSupplier(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Comercializadora"
                      placeholder="Todas..."
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  sx={{ width: { xs: "100%", sm: 250 }, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />

                {/* Filtro por Cliente */}
                <Autocomplete
                  size="small"
                  options={switchClients}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option.type === "company"
                      ? option.companyName
                      : `${option.firstName} ${option.lastName}`;
                  }}
                  value={selectedClient}
                  onChange={(event, newValue) => setSelectedClient(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      placeholder="Todos..."
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  sx={{ width: { xs: "100%", sm: 250 }, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <TextField
                  size="small"
                  type="date"
                  label="Fecha desde"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: "100%", sm: 200 }, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />

                <TextField
                  size="small"
                  type="date"
                  label="Fecha hasta"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: "100%", sm: 200 }, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />

                {activeFiltersCount > 0 && (
                  <Button
                    size="small"
                    startIcon={<Clear />}
                    onClick={clearAllFilters}
                    sx={{
                      height: 40,
                      borderRadius: "12px",
                      textTransform: "none",
                      color: theme.palette.error.main,
                      "&:hover": { backgroundColor: `${theme.palette.error.main}10` },
                    }}
                  >
                    Limpiar filtros
                  </Button>
                )}
              </Stack>

              {/* Chips de filtros activos */}
              {activeFiltersCount > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {statusFilter !== "all" && (
                    <Chip
                      size="small"
                      label={statusFilter === "active" ? "Activos" : "Finalizados"}
                      onDelete={() => setStatusFilter("all")}
                      sx={{ borderRadius: "8px", backgroundColor: `${theme.palette.primary.main}20`, color: theme.palette.primary.main, fontWeight: 600 }}
                    />
                  )}
                  {supplyTypeFilter !== "all" && (
                    <Chip
                      size="small"
                      icon={supplyTypeFilter === SUPPLY_TYPES.GAS ? <LocalFireDepartment sx={{ fontSize: 14 }} /> : <ElectricBolt sx={{ fontSize: 14 }} />}
                      label={supplyTypeFilter === SUPPLY_TYPES.GAS ? "Gas" : "Luz"}
                      onDelete={() => setSupplyTypeFilter("all")}
                      sx={{
                        borderRadius: "8px",
                        fontWeight: 600,
                        backgroundColor: supplyTypeFilter === SUPPLY_TYPES.GAS
                          ? `${theme.palette.error.main}20`
                          : `${theme.palette.warning.main}20`,
                        color: supplyTypeFilter === SUPPLY_TYPES.GAS
                          ? theme.palette.error.main
                          : theme.palette.warning.main,
                      }}
                    />
                  )}
                  {selectedSupplier && (
                    <Chip
                      size="small"
                      icon={<Business sx={{ fontSize: 14 }} />}
                      label={selectedSupplier.name}
                      onDelete={() => setSelectedSupplier(null)}
                      sx={{ borderRadius: "8px", backgroundColor: `${theme.palette.primary.main}20`, color: theme.palette.primary.main, fontWeight: 600 }}
                    />
                  )}
                  {selectedClient && (
                    <Chip
                      size="small"
                      icon={<SwapHoriz sx={{ fontSize: 14 }} />}
                      label={selectedClient.type === "company" ? selectedClient.companyName : `${selectedClient.firstName} ${selectedClient.lastName}`}
                      onDelete={() => setSelectedClient(null)}
                      sx={{ borderRadius: "8px", backgroundColor: `${theme.palette.primary.main}20`, color: theme.palette.primary.main, fontWeight: 600 }}
                    />
                  )}
                  {dateFrom && (
                    <Chip
                      size="small"
                      icon={<Event sx={{ fontSize: 14 }} />}
                      label={`Desde: ${formatDate(dateFrom)}`}
                      onDelete={() => setDateFrom("")}
                      sx={{ borderRadius: "8px", backgroundColor: `${theme.palette.primary.main}20`, color: theme.palette.primary.main, fontWeight: 600 }}
                    />
                  )}
                  {dateTo && (
                    <Chip
                      size="small"
                      icon={<Event sx={{ fontSize: 14 }} />}
                      label={`Hasta: ${formatDate(dateTo)}`}
                      onDelete={() => setDateTo("")}
                      sx={{ borderRadius: "8px", backgroundColor: `${theme.palette.primary.main}20`, color: theme.palette.primary.main, fontWeight: 600 }}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      {/* Tabla */}
      <Card
        elevation={2}
        sx={{
          borderRadius: { xs: 1, sm: 2 },
          overflow: "hidden",
          border: "1px solid",
          borderColor: theme.palette.divider,
          background: theme.palette.background.paper,
        }}
      >
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow sx={{
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(0, 175, 163, 0.1)"
      : "rgba(0, 95, 101, 0.05)",
}}>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                  Cliente
                </TableCell>
                {!isMobile && (
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}>
                    Comercializadora
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}>
                    Tipo
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}>
                    Dirección
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                  F. Entrada
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                  F. Salida
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                  Estado
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: { xs: 80, sm: 100 }, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 8} align="center" sx={{ py: 8 }}>
                    <SwapHoriz sx={{ fontSize: 64, color: theme.palette.primary.main, opacity: 0.3, mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
                      {activeFiltersCount > 0 ? "Sin resultados" : "Sin trámites"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: activeFiltersCount > 0 ? 2 : 0 }}>
                      {activeFiltersCount > 0 ? "Prueba a cambiar los filtros" : "Añade tu primer trámite de cambio de comercializadora"}
                    </Typography>
                    {activeFiltersCount > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={clearAllFilters}
                        sx={{ borderRadius: "12px", textTransform: "none", mt: 1 }}
                      >
                        Limpiar filtros
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSwitches?.map((switchItem) => {
                  const isGas = (switchItem.supplyType || SUPPLY_TYPES.LUZ) === SUPPLY_TYPES.GAS;
                  
                  return (
                    <TableRow
                      key={switchItem.id}
                      hover
                      sx={{
                        borderLeft: `4px solid ${isActiveSwitch(switchItem) ? theme.palette.success.main : theme.palette.error.main}`,
                        backgroundColor: isActiveSwitch(switchItem)
                          ? `${theme.palette.success.main}08`
                          : `${theme.palette.error.main}08`,
                        "&:hover": {
                          backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255, 152, 0, 0.05)",
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                          {switchItem.switchClientName || "Sin nombre"}
                        </Typography>
                        {isMobile && (
                          <Stack spacing={0.3} sx={{ mt: 0.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Business sx={{ fontSize: 12, color: theme.palette.primary.main }} />
                              <Typography variant="caption" color="text.secondary">
                                {switchItem.supplierName || "—"}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {getSupplyTypeChip(switchItem)}
                            </Box>
                          </Stack>
                        )}
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Business sx={{ fontSize: 14, color: theme.palette.primary.main }} />
                            <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                              {switchItem.supplierName || "—"}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell>
                          {getSupplyTypeChip(switchItem)}
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LocationOn sx={{ fontSize: 14, color: theme.palette.warning.main, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ fontSize: "0.8rem", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {getFormattedAddress(switchItem)}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Event sx={{ fontSize: 14, color: theme.palette.success.main }} />
                          <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                            {formatDate(switchItem.fechaEntrada)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <EventBusy sx={{ fontSize: 14, color: switchItem.fechaSalida ? theme.palette.error.main : theme.palette.text.disabled }} />
                          <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                            {formatDate(switchItem.fechaSalida)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={isActiveSwitch(switchItem) ? "Activo" : "Finalizado"}
                          sx={{
                            backgroundColor: isActiveSwitch(switchItem) ? theme.palette.success.main : theme.palette.error.main,
                            color: "white",
                            fontWeight: 600,
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); onEdit(switchItem); }}
                              sx={{ color: theme.palette.primary.main }}
                            >
                              <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(switchItem); }}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {sorted?.length > 0 && (
          <TablePagination
            component="div"
            count={sorted?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              borderTop: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiTablePagination-toolbar": { minHeight: 48, px: { xs: 1, sm: 2 }, fontSize: { xs: "0.75rem", sm: "0.85rem" } },
            }}
          />
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setLocalSwitches((prev) => prev.filter((s) => s.id !== deleteTarget.id));
          const deletedSwitch = deleteTarget;
          setDeleteTarget(null);
          try {
            await remove(deletedSwitch.id);
          } catch (error) {
            console.error("Error deleting:", error);
            setLocalSwitches((prev) => [...prev, deletedSwitch]);
          }
        }}
        title="Eliminar Trámite"
        message={`¿Eliminar el trámite de ${deleteTarget?.switchClientName || "este cliente"}?`}
        type="delete"
      />
    </Box>
  );
};

export default SwitchList;